import { Component } from './component.js';

export class InteractiveGallery extends Component {
  requiredRefs = ['container', 'canvas', 'cards'];

  _canvas   = null;
  _ctx      = null;
  _DPR      = 1;

  _offsetX  = 0;
  _offsetY  = 0;
  _scale    = 2.3;

  _isDragging    = false;
  _dragStartX    = 0;
  _dragStartY    = 0;
  _dragDistance  = 0;
  _pointerStart  = { x: 0, y: 0 };
  _pointerOnCanvas = false;

  _hoverIndex    = -1;
  _mouseWorld    = { x: -99999, y: -99999 };
  _productScales = [];
  _proximities   = [];
  _captionAlphas = [];

  _velocity    = { x: 0, y: 0 };
  _lastDragPos = [];
  _rafId       = null;

  _isModalOpen          = false;
  _selectedProductIndex = -1;
  _modalEl              = null;

  _pinchActive     = false;
  _pinchStartDist  = 0;
  _pinchStartScale = 1;
  _lastTap         = null;   // {x, y, t} of the last empty-space tap, for double-tap zoom

  _isMobile       = false;   // decided once at mount; mobile gets the lookbook, not the canvas
  _mobileSlides   = null;
  _mobileScrollEl = null;
  _mobilePanel    = null;
  _mobileRaf      = null;

  _cardW         = 0;
  _cardH         = 0;
  _cardPositions = [];
  _boundRadius   = 0;

  HOVER_SCALE = 1.15;
  HOVER_SPEED = 0.08;
  DRAG_THRESH = 5;
  SCALE_MIN   = 1.5;
  SCALE_MAX   = 3;
  SPREAD      = 1.32;  // ring spacing as a multiple of card size (higher = more air)

  get _config() {
    return {
      cardWidth:       parseInt(this.dataset.cardWidth)       || 200,
      gapBetweenCards: parseInt(this.dataset.gapBetweenCards) || 15,
      imageScale:      parseInt(this.dataset.imageScale)      || 60,   // % of card
      imagePadding:    parseInt(this.dataset.imagePadding)    || 28,   // px
    };
  }

  get _isWishlistOnly() {
    return this.dataset.wishlistOnly === 'true';
  }

  // Palmer-style: only the product photo floats (no card box / shadow / baked text).
  // Wishlist mode always renders photo-only; a standalone gallery can opt in via data-photo-only.
  get _isPhotoOnly() {
    return this.dataset.photoOnly === 'true' || this._isWishlistOnly;
  }

  // =========================================================================
  // LIFECYCLE
  // =========================================================================

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.canvas || !this.refs['cards']) {
      requestAnimationFrame(() => this.connectedCallback());
      return;
    }

    this._canvas = this.refs.canvas;

    // Mobile gets a completely different experience: a vertical, snap-scrolling
    // editorial lookbook (one full-screen product per slide with parallax drift)
    // instead of the desktop drag-to-explore canvas. Decided once at mount.
    this._isMobile = window.innerWidth < 750;
    if (this._isMobile) {
      this._initMobileFeed();
      return;
    }

    this._ctx    = this._canvas.getContext('2d');
    // Cap DPR at 2: on DPR-3 phones (iPhone Pro) an uncapped backing store is ~2.25×
    // the pixels to shade every frame — the main cause of drag/pinch jank on mobile,
    // with no visible sharpness gain at this viewing distance.
    this._DPR    = Math.min(2, Math.max(1, window.devicePixelRatio || 1));

    const vw = window.innerWidth;
    const sizeMultiplier = vw < 480 ? 0.55 : vw < 750 ? 0.72 : vw < 1200 ? 1.0 : 1.15;
    this._cardW = this._config.cardWidth * 1.33 * sizeMultiplier;
    this._cardH = this._cardW * 1.25;
    this._sizeMult = sizeMultiplier;   // reused for per-card px settings (padding, concept margin)
    // Image padding (px from the section setting), scaled to the card like everything else
    this._imgPad = this._config.imagePadding * sizeMultiplier;
    // Per-section ring density (how far apart products sit). Falls back to the class default.
    if (this.dataset.spread) {
      const s = parseFloat(this.dataset.spread);
      if (s > 0) this.SPREAD = s;
    }

    if (this.dataset.wishlistOnly === 'true') {
      this._filterToWishlist();
    }

    // Randomize which product lands in which slot on every page load.
    this._shuffleInPlace(this.refs['cards']);

    // Ring 0 sits dead-centre and the initial view frames it — never a concept
    // image there, so a product always greets you on load.
    const firstProduct = this.refs['cards'].findIndex(c => c.dataset.concept !== 'true');
    if (firstProduct > 0) {
      const tmp = this.refs['cards'][0];
      this.refs['cards'][0] = this.refs['cards'][firstProduct];
      this.refs['cards'][firstProduct] = tmp;
    }

    // Ensure the Dymo caption font is loaded so the canvas renders it (not a fallback),
    // then precompute every caption's fitted size once — off the hover path — so hovering
    // a new card doesn't pay a one-time measureText cost.
    if (this.dataset.photoOnly === 'true' && document.fonts) {
      document.fonts.load('400 20px "LargeTitleTest"')
        .then(() => document.fonts.load('400 20px "GalleryTitle"'))
        .then(() => this._precomputeCaptionSizes());
    }

    this._productScales = this.refs['cards'].map(() => 1);
    this._proximities   = this.refs['cards'].map(() => 0);
    this._captionAlphas = this.refs['cards'].map(() => 0);
    this._buildLayout();
    this._resizeCanvas();
    this._fitAllProducts();

    if (this._isWishlistOnly) {
      const ov = document.createElement('div');
      ov.className = 'ig-title-overlay';
      ov.setAttribute('aria-hidden', 'true');
      ov.innerHTML = '<span class="ig-title-overlay__name"></span>';
      document.body.appendChild(ov);
      this._titleOverlay = ov;
    }

    // Hard-lock page scroll while the gallery is mounted (CSS :has lock proved
    // unreliable), so the wheel only ever zooms and never scrolls the page.
    this._prevHtmlOverflow = document.documentElement.style.overflow;
    this._prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    window.addEventListener('resize', this._onResize);
    window.addEventListener('wheel',  this._onWheel, { passive: false });

    document.addEventListener('pointerdown',   this._onPointerDown);
    document.addEventListener('pointermove',   this._onPointerMove, { passive: false });
    document.addEventListener('pointerup',     this._onPointerUp);
    document.addEventListener('pointercancel', this._onPointerUp);

    this._canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this._canvas.addEventListener('touchmove',  this._onTouchMove,  { passive: false });
    this._canvas.addEventListener('touchend',   this._onTouchEnd);

    setTimeout(() => requestAnimationFrame(this._loop), 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this._isMobile) {
      if (this._mobileScrollEl) this._mobileScrollEl.removeEventListener('scroll', this._onMobileScroll);
      window.removeEventListener('resize', this._onMobileResize);
      if (this._mobileRaf) cancelAnimationFrame(this._mobileRaf);
      if (this._mobilePanel) { this._mobilePanel.remove(); this._mobilePanel = null; }
      if (this._modalEl) { this._modalEl.remove(); this._modalEl = null; }
      return;
    }

    document.documentElement.style.overflow = this._prevHtmlOverflow || '';
    document.body.style.overflow = this._prevBodyOverflow || '';
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('wheel',  this._onWheel);
    document.removeEventListener('pointerdown',   this._onPointerDown);
    document.removeEventListener('pointermove',   this._onPointerMove);
    document.removeEventListener('pointerup',     this._onPointerUp);
    document.removeEventListener('pointercancel', this._onPointerUp);
    this._canvas.removeEventListener('touchstart', this._onTouchStart);
    this._canvas.removeEventListener('touchmove',  this._onTouchMove);
    this._canvas.removeEventListener('touchend',   this._onTouchEnd);
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._titleOverlay) { this._titleOverlay.remove(); this._titleOverlay = null; }
  }

  // =========================================================================
  // LAYOUT
  // =========================================================================

  _filterToWishlist() {
    let wishlist = [];
    try { wishlist = JSON.parse(localStorage.getItem('jd_wishlist') || '[]'); } catch {}

    // Wishlist entries can be either bare ID strings (legacy) or {id, handle} objects (new).
    const wishlistIds = new Set(
      wishlist.map(e => (typeof e === 'string') ? e : (e && e.id))
    );

    const allCards = this.refs['cards'];
    const filtered = allCards.filter(card => wishlistIds.has(card.dataset.productId));
    this.refs['cards'] = filtered;

    const el = document.getElementById('product-count-total');
    if (el) el.textContent = filtered.length;

    if (filtered.length === 0) {
      this._showEmptyWishlist();
    }
  }

  _showEmptyWishlist() {
    const msg = document.createElement('div');
    msg.style.cssText = [
      'position:absolute', 'inset:0', 'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center', 'gap:16px',
      'font-family:\'Hellix-Regular\',sans-serif', 'color:rgba(25,27,28,0.55)',
      'pointer-events:none', 'z-index:10',
    ].join(';');
    msg.innerHTML = `
      <span style="font-size:48px;line-height:1;opacity:0.4">✶</span>
      <p style="margin:0;font-size:16px;letter-spacing:0.02em">No saved products yet</p>
      <p style="margin:0;font-size:13px;opacity:0.7">Mark products with ✶ to save them here</p>
    `;
    this.appendChild(msg);
  }

  _buildLayout() {
    const count       = this.refs['cards'].length;
    // Palmer-style: generous air between items so you drag to explore rather than
    // see everything at once. Spacing keyed to the *drawn* card size, not the raw setting.
    const ringSpacing = this._cardW * this.SPREAD + this._config.gapBetweenCards;
    const jitter      = ringSpacing * 0.22;
    const rand        = (m) => (Math.random() * 2 - 1) * m;
    this._cardPositions = [];
    let placed = 0, ring = 0;

    while (placed < count) {
      if (ring === 0) {
        this._cardPositions.push({ x: -this._cardW / 2, y: -this._cardH / 2 });
        placed++; ring++;
        continue;
      }
      // Fewer slots per ring + a random start angle + positional jitter → organic,
      // non-gridded scatter instead of neat concentric rings.
      const slots       = Math.max(6, Math.round(ring * 5.5));
      const radius      = ring * ringSpacing;
      const angleOffset = Math.random() * Math.PI * 2;
      for (let i = 0; i < slots && placed < count; i++) {
        const angle = angleOffset + (i / slots) * Math.PI * 2;
        this._cardPositions.push({
          x: Math.cos(angle) * radius - this._cardW / 2 + rand(jitter),
          y: Math.sin(angle) * radius - this._cardH / 2 + rand(jitter),
        });
        placed++;
      }
      ring++;
    }

    // Resolve overlaps: every card keeps a margin so images never collide.
    this._separateCards();

    // Radius of the product cluster (farthest card centre from origin) — used to
    // constrain panning so you can't drift off into empty space.
    let maxR = 0;
    for (const p of this._cardPositions) {
      maxR = Math.max(maxR, Math.hypot(p.x + this._cardW / 2, p.y + this._cardH / 2));
    }
    this._boundRadius = maxR;
  }

  // Relaxation pass (axis-aligned): separate every pair of cards so their image
  // boxes never overlap. Products keep a small margin (~5px gap); concept images
  // keep their larger "clear space" from the block setting.
  _separateCards() {
    const cards = this.refs['cards'];
    const pos   = this._cardPositions;
    const n     = cards.length;
    if (!n || !pos.length) return;

    const mult = this._sizeMult || 1;
    const hw = new Array(n);   // half-width  keep-out (image box + margin)
    const hh = new Array(n);   // half-height keep-out
    for (let i = 0; i < n; i++) {
      const c = cards[i];
      const isConcept = c.dataset.concept === 'true';
      const scale = c.dataset.cardScale ? parseFloat(c.dataset.cardScale) / 100
                                        : (this._isPhotoOnly ? 0.92 : 0.65);
      // Products: 2.5px each side -> ~5px gap. Concepts: their block margin.
      const margin = (isConcept ? (parseFloat(c.dataset.margin) || 0) : 2.5) * mult;
      hw[i] = this._cardW * scale * 0.5 + margin;
      hh[i] = this._cardH * scale * 0.5 + margin;
    }

    const cx = (k) => pos[k].x + this._cardW / 2;
    const cy = (k) => pos[k].y + this._cardH / 2;

    for (let iter = 0; iter < 14; iter++) {
      let moved = false;
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const dx = cx(j) - cx(i);
          const dy = cy(j) - cy(i);
          const ox = (hw[i] + hw[j]) - Math.abs(dx);   // x overlap
          const oy = (hh[i] + hh[j]) - Math.abs(dy);   // y overlap
          if (ox <= 0 || oy <= 0) continue;            // boxes clear
          moved = true;
          if (ox < oy) {                                // push along the shallower axis
            const push = ox * 0.5 * (dx < 0 ? -1 : 1);
            pos[i].x -= push; pos[j].x += push;
          } else {
            const push = oy * 0.5 * (dy < 0 ? -1 : 1);
            pos[i].y -= push; pos[j].y += push;
          }
        }
      }
      if (!moved) break;
    }
  }

  // Dymo label look: words separated by dots instead of spaces.
  _captionWords(title) {
    return (title.textContent || '').trim().split(/\s+/).filter(Boolean);
  }

  // Greedily wraps caption words into lines that each fit maxW at the
  // current ctx.font, keeping the dot-separated Dymo look within a line.
  _wrapCaptionWords(ctx, words, maxW) {
    const lines = [];
    let current = [];
    for (const word of words) {
      const trial = [...current, word].join('.');
      if (current.length && ctx.measureText(trial).width > maxW) {
        lines.push(current.join('.'));
        current = [word];
      } else {
        current.push(word);
      }
    }
    if (current.length) lines.push(current.join('.'));
    return lines;
  }

  // Fit every caption's Dymo font size up front (once), so it never happens
  // during hover. Uses a throwaway context to avoid touching the render ctx.
  _precomputeCaptionSizes() {
    const mctx = document.createElement('canvas').getContext('2d');
    if (!mctx) return;
    const maxTextW = this._cardW - 24;
    for (const card of this.refs['cards']) {
      const title = card.querySelector('.gallery-card__title');
      if (!title) continue;
      const words = this._captionWords(title);
      let fs = 30;
      mctx.font = `400 ${fs}px 'LargeTitleTest', 'GalleryTitle', cursive`;
      while (mctx.measureText(words.join('.')).width > maxTextW && fs > 14) {
        fs -= 1;
        mctx.font = `400 ${fs}px 'LargeTitleTest', 'GalleryTitle', cursive`;
      }
      card._captionFs = fs;
      card._captionLines = this._wrapCaptionWords(mctx, words, maxTextW);
    }
  }

  _shuffleInPlace(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  _resizeCanvas() {
    const rect = this._canvas.getBoundingClientRect();
    this._canvas.width  = Math.floor(rect.width  * this._DPR);
    this._canvas.height = Math.floor(rect.height * this._DPR);
    this._ctx.setTransform(this._DPR, 0, 0, this._DPR, 0, 0);
  }

  _fitAllProducts() {
    if (!this._cardPositions.length) return;
    const rect = this._canvas.getBoundingClientRect();
    const cW = rect.width;
    const cH = rect.height;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of this._cardPositions) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + this._cardW);
      maxY = Math.max(maxY, p.y + this._cardH);
    }

    const bboxW = maxX - minX;
    const bboxH = maxY - minY;
    const fitScale = Math.min(
      (cW / bboxW) * 0.85,
      (cH / bboxH) * 0.85
    );

    const centroidX = (minX + maxX) / 2;
    const centroidY = (minY + maxY) / 2;

    // Zoom-out limit = whole spread visible. Initial view = cards at a comfortable
    // size with lots of surrounding air, so you drag to explore (Palmer feel)
    // instead of the entire collection being crammed into one screen.
    // Capped at 3x fitScale so you can't zoom out as far as the full spread.
    const minScale = fitScale * 3;
    this.SCALE_MIN = minScale;
    const targetCardPx = Math.min(cW, cH) * 0.38;
    const viewScale    = targetCardPx / this._cardW;
    this._scale   = Math.min(Math.max(viewScale, minScale), this.SCALE_MAX);
    this._offsetX = -centroidX * this._scale;
    this._offsetY = -centroidY * this._scale;
  }

  _onResize = () => { this._resizeCanvas(); this._fitAllProducts(); };

  // =========================================================================
  // RENDER LOOP
  // =========================================================================

  _loop = () => {
    this._draw();
    this._rafId = requestAnimationFrame(this._loop);
  };

  _draw() {
    const cards = this.refs['cards'];
    const cW    = this._canvas.width  / this._DPR;
    const cH    = this._canvas.height / this._DPR;
    const ctx   = this._ctx;

    // Clear to transparent so the element's CSS background shows through — that's
    // where the page backdrop colour lives (white on desktop, grey on mobile).
    // Keeping the backdrop OUT of the canvas keeps it clear of the brightness(1.07)
    // filter (which would otherwise clamp any near-white grey back to pure white);
    // the product images still get that brightness.
    ctx.clearRect(0, 0, cW, cH);

    this._clampOffset();   // keep panning within the product circle

    ctx.save();
    ctx.translate(cW / 2 + this._offsetX, cH / 2 + this._offsetY);
    ctx.scale(this._scale, this._scale);

    for (let i = 0; i < this._productScales.length; i++) {
      const p = this._cardPositions[i];
      const cx = p.x + this._cardW / 2;
      const cy = p.y + this._cardH / 2;
      const dist = Math.hypot(this._mouseWorld.x - cx, this._mouseWorld.y - cy);
      const proximity = Math.max(0, 1 - dist / (this._cardW * 0.7));
      this._proximities[i] += (proximity - this._proximities[i]) * this.HOVER_SPEED;
      const isConcept = cards[i]?.dataset?.concept === 'true';
      const cardScaleRatio = parseFloat(cards[i]?.dataset?.cardScale || '100') / 100;
      const maxHover = this._isWishlistOnly ? 1 : 1 + (this.HOVER_SCALE - 1) / Math.sqrt(cardScaleRatio);
      // Concept images stay static — no hover zoom.
      const target = isConcept ? 1 : 1 + (maxHover - 1) * Math.pow(proximity, 2.5);
      this._productScales[i] += (target - this._productScales[i]) * this.HOVER_SPEED;

      // Caption opacity is a time-based hover state (not mouse-distance): the
      // hovered card fades smoothly to full, everything else fades out.
      const capTarget = (i === this._hoverIndex) ? 1 : 0;
      this._captionAlphas[i] += (capTarget - this._captionAlphas[i]) * 0.1;
    }

    const cullRect = this._visibleWorldRect(this._cardW);        // draw margin
    const loadRect = this._visibleWorldRect(this._cardH * 5);    // preload margin — lead time for decode

    const hi = this._hoverIndex;
    cards.forEach((card, i) => {
      if (i >= this._cardPositions.length || i === hi) return;
      const p = this._cardPositions[i];
      this._maybeLoadImage(card, p, loadRect);          // lazy-load when near view
      if (this._isCardOutside(p, cullRect)) return;     // skip off-screen cards
      this._drawCard(card, p.x, p.y, this._productScales[i], this._proximities[i], this._captionAlphas[i]);
    });
    if (hi >= 0 && hi < this._cardPositions.length) {
      const p = this._cardPositions[hi];
      this._maybeLoadImage(cards[hi], p, loadRect);
      this._drawCard(cards[hi], p.x, p.y, this._productScales[hi], this._proximities[hi], this._captionAlphas[hi]);
    }

    ctx.restore();

    if (this._titleOverlay) this._updateTitleOverlay();
  }

  _updateTitleOverlay() {
    const overlay = this._titleOverlay;
    if (!overlay) return;

    let bestProx = 0, bestIdx = -1;
    for (let i = 0; i < this._cardPositions.length; i++) {
      const p    = this._cardPositions[i];
      const dist = Math.hypot(this._mouseWorld.x - (p.x + this._cardW / 2), this._mouseWorld.y - (p.y + this._cardH / 2));
      const prox = Math.max(0, 1 - dist / (this._cardW * 0.7));
      if (prox > bestProx) { bestProx = prox; bestIdx = i; }
    }

    if (bestIdx < 0 || bestProx <= 0) {
      overlay.style.opacity = '0';
      return;
    }

    const p    = this._cardPositions[bestIdx];
    const card = this.refs['cards'][bestIdx];
    const name = overlay.querySelector('.ig-title-overlay__name');
    if (name) name.textContent = card.querySelector('.gallery-card__title')?.textContent || '';

    const rect   = this._canvas.getBoundingClientRect();
    const cW     = this._canvas.width / this._DPR;
    const cH     = this._canvas.height / this._DPR;
    const screenX = rect.left + cW / 2 + this._offsetX + (p.x + this._cardW / 2) * this._scale;
    const screenY = rect.top  + cH / 2 + this._offsetY + (p.y + this._cardH * 0.80) * this._scale;
    overlay.style.left    = `${screenX}px`;
    overlay.style.top     = `${screenY}px`;
    overlay.style.opacity = Math.sqrt(bestProx).toFixed(3);
  }

  // =========================================================================
  // VISIBILITY / LAZY LOADING
  // =========================================================================

  _visibleWorldRect(marginPx = 0) {
    const cW = this._canvas.width  / this._DPR;
    const cH = this._canvas.height / this._DPR;
    const m  = marginPx / this._scale;
    return {
      left:   (-cW / 2 - this._offsetX) / this._scale - m,
      right:  ( cW / 2 - this._offsetX) / this._scale + m,
      top:    (-cH / 2 - this._offsetY) / this._scale - m,
      bottom: ( cH / 2 - this._offsetY) / this._scale + m,
    };
  }

  _isCardOutside(p, rect) {
    return p.x > rect.right  || p.x + this._cardW < rect.left ||
           p.y > rect.bottom || p.y + this._cardH < rect.top;
  }

  // Circular pan boundary: keep the viewport centre within the product circle so
  // you can't drift off into empty space. The allowed radius shrinks with the
  // viewport size, so when zoomed out the cluster stays roughly centred.
  _clampOffset() {
    if (!this._boundRadius) return;
    const cW = this._canvas.width  / this._DPR;
    const cH = this._canvas.height / this._DPR;
    const margin = Math.min((cW / 2), (cH / 2)) / this._scale;
    const R = Math.max(0, this._boundRadius - margin) + this._cardW * 1.5;   // a bit more roam

    const wx   = -this._offsetX / this._scale;   // world point at screen centre
    const wy   = -this._offsetY / this._scale;
    const dist = Math.hypot(wx, wy);
    if (dist > R) {
      const k = R / dist;
      this._offsetX = -wx * k * this._scale;
      this._offsetY = -wy * k * this._scale;
    }
  }

  // Assigns src (from data-src) only once the card is near the viewport, so
  // off-screen product images are never fetched until you drag toward them.
  _maybeLoadImage(card, p, rect) {
    const img = card.querySelector('img');
    if (!img || img.getAttribute('src') || !img.dataset.src) return;
    if (this._isCardOutside(p, rect)) return;
    img.src = img.dataset.src;
    // Decode off the render path so the first drawImage doesn't stall the frame
    // (the hover/navigation hitch was a synchronous decode of a just-loaded image).
    if (img.decode) img.decode().catch(() => {});
  }

  // =========================================================================
  // DRAW CARD
  // =========================================================================

  _drawCard(card, x, y, s = 1, proximity = 0, captionAlpha = 0) {
    const ctx = this._ctx;
    ctx.filter = 'none';   // brightness handled by a CSS filter on the canvas (cheap); no per-draw filter
    const w   = this._cardW;
    const h   = this._cardH;
    const img   = card.querySelector('img');
    const title = card.querySelector('.gallery-card__title');
    const price = card.querySelector('.gallery-card__price');

    const isConcept    = card.dataset.concept === 'true';
    const conceptTilt  = isConcept ? (card._conceptTilt ??= (Math.random() * 2 - 1) * 0.03) : 0;  // ±~1.7°, stable per card

    ctx.save();
    ctx.translate(x + w / 2, y + h / 2);
    if (conceptTilt) ctx.rotate(conceptTilt);
    ctx.scale(s, s);
    ctx.translate(-(x + w / 2), -(y + h / 2));

    if (!this._isPhotoOnly) {
      ctx.shadowColor   = 'rgba(0,0,0,0.15)';
      ctx.shadowBlur    = 8;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle     = '#ffffff';
      this._rrect(x, y, w, h, 0);
      ctx.fill();
      ctx.shadowColor = 'transparent';
    }

    // Image area
    const hasCaption = this.dataset.photoOnly === 'true';   // Dymo caption at card bottom (explore-all only)
    const textH    = hasCaption ? 40 : (this._isPhotoOnly ? 0 : 56);
    // Section-controlled image padding, clamped so the image area can't collapse on small cards
    const pad      = Math.max(0, Math.min(this._imgPad, (w - 40) / 2, (h - textH - 40) / 2));
    const areaW    = w - pad * 2;
    const areaH    = h - textH - pad * 2;
    const cardScaleAttr = card.dataset.cardScale ? parseFloat(card.dataset.cardScale) / 100 : null;
    const imgScale = cardScaleAttr ?? (this._isPhotoOnly ? 0.92 : 0.65);
    const maxW     = areaW * imgScale;
    const maxH     = areaH * imgScale;

    let imgBottomY = y + h - textH;   // fallback anchor for the caption
    if (img && img.complete && img.naturalWidth > 0) {
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const sc = Math.min(maxW / iw, maxH / ih);
      const fw = iw * sc;
      const fh = ih * sc;
      const imgTopY = y + pad + (areaH - fh) / 2;
      ctx.drawImage(img, x + (w - fw) / 2, imgTopY, fw, fh);
      imgBottomY = imgTopY + fh;
    }

    if (!this._isPhotoOnly) {
      ctx.filter = 'none';
      ctx.textBaseline = 'top';
      ctx.font         = "bolder 13px 'Hellix-Regular', sans-serif";
      ctx.fillStyle    = 'rgba(28,28,28,1.0)';
      if (title) ctx.fillText(title.textContent, x + 12, y + h - 40);
      ctx.font      = "600 12px 'Hellix-Light', sans-serif";
      ctx.fillStyle = 'rgba(60,60,60,1.0)';
      if (price) ctx.fillText(price.textContent, x + 12, y + h - 18);
    }

    ctx.restore();

    // Caption is drawn AFTER restore so it stays outside the hover scale (`s`):
    // static size and position while the photo grows on hover.
    if (hasCaption && title && captionAlpha > 0.01) {
      // Fit the font size and line wrap ONCE per card (title + width are constant)
      // instead of re-measuring every frame — the per-frame measureText/font churn
      // was the source of the hover hitch. Only cache once the Dymo font is loaded
      // so the measured metrics are correct.
      let fs    = card._captionFs;
      let lines = card._captionLines;
      if (fs === undefined || lines === undefined) {
        const words = this._captionWords(title);
        fs = 30;
        const maxTextW = w - 24;
        ctx.font = `400 ${fs}px 'LargeTitleTest', 'GalleryTitle', cursive`;
        while (ctx.measureText(words.join('.')).width > maxTextW && fs > 14) {
          fs -= 1;
          ctx.font = `400 ${fs}px 'LargeTitleTest', 'GalleryTitle', cursive`;
        }
        lines = this._wrapCaptionWords(ctx, words, maxTextW);
        const fontReady = !document.fonts || document.fonts.check('20px "LargeTitleTest"');
        if (fontReady) {
          card._captionFs    = fs;
          card._captionLines = lines;
        }
      }

      const tilt     = (card._captionTilt ??= (Math.random() * 2 - 1) * 0.05);  // ±~3°, stable per card
      // Anchor is the baseline of the FIRST line, sitting just below the photo;
      // wrapped lines stack downward from there.
      const lineHeight   = fs * 1.15;
      const firstBaseline = imgBottomY + fs * 0.85;

      // Offset "echo" duplicate: the label is drawn twice — a shifted ghost copy
      // behind, then the main copy on top — for a chromatic/glitch look.
      // Colours come from the section settings (data-attributes on the element).
      const CAP_MAIN_COLOR  = this.dataset.captionColor     || '#2c2c2c';   // front copy
      const CAP_ECHO_COLOR  = this.dataset.captionEchoColor || '#3a3a3a';   // shifted copy behind
      const CAP_ECHO_DX      = fs * 0.03;  // horizontal offset (scales with font size)
      const CAP_ECHO_DY      = fs * 0.03;  // vertical offset

      ctx.save();
      ctx.filter       = 'none';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.font         = `400 ${fs}px 'LargeTitleTest', 'GalleryTitle', cursive`;
      ctx.globalAlpha  = Math.min(1, captionAlpha);   // time-based hover fade
      ctx.translate(x + w / 2, firstBaseline);
      ctx.rotate(tilt);
      for (let li = 0; li < lines.length; li++) {
        const dy = li * lineHeight;
        // Ghost copy behind
        ctx.fillStyle = CAP_ECHO_COLOR;
        ctx.fillText(lines[li], CAP_ECHO_DX, dy + CAP_ECHO_DY);
        // Main copy on top
        ctx.fillStyle = CAP_MAIN_COLOR;
        ctx.fillText(lines[li], 0, dy);
      }
      ctx.restore();
    }
  }

  // =========================================================================
  // ROUNDED RECT
  // =========================================================================

  _rrect(x, y, w, h, r) {
    const ctx = this._ctx;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  // =========================================================================
  // COORDINATE HELPERS
  // =========================================================================

  _toWorld(screenX, screenY) {
    const rect = this._canvas.getBoundingClientRect();
    const cW   = this._canvas.width  / this._DPR;
    const cH   = this._canvas.height / this._DPR;
    return {
      x: (screenX - rect.left - cW / 2 - this._offsetX) / this._scale,
      y: (screenY - rect.top  - cH / 2 - this._offsetY) / this._scale,
    };
  }

  _toCanvas(screenX, screenY) {
    const rect = this._canvas.getBoundingClientRect();
    return { x: screenX - rect.left, y: screenY - rect.top };
  }

  _isOverCanvas(screenX, screenY) {
    const rect = this._canvas.getBoundingClientRect();
    return screenX >= rect.left && screenX <= rect.right &&
           screenY >= rect.top  && screenY <= rect.bottom;
  }

  // True when the event landed on an overlay stacked above the gallery (header
  // menu, footer sheet, reveal handle, modal) — used to stop clicks/scroll from
  // reaching the products behind them, without assuming the canvas is e.target.
  _isOverlayTarget(target) {
    return !!(target && target.closest && target.closest(
      '.shopify-section-group-header-group, .shopify-section-group-footer-group, ' +
      '.ig-footer-handle, .ig-modal-backdrop, .menu-drawer, header, dialog'
    ));
  }

  // A transparent full-page layer (e.g. the header logo's fluid canvas) can sit
  // above our canvas and become the real cursor surface, so setting the cursor on
  // our own element never shows. Apply it to whatever is actually on top too.
  _setCursor(c) {
    this.style.cursor = c;
    if (this._canvas) this._canvas.style.cursor = c;
    if (this._surfaceEl && this._surfaceEl !== this && this._surfaceEl !== this._canvas) {
      this._surfaceEl.style.cursor = c;
    }
  }

  _cardAt(wx, wy) {
    for (let i = this._cardPositions.length - 1; i >= 0; i--) {
      const p = this._cardPositions[i];
      if (wx >= p.x && wx <= p.x + this._cardW &&
          wy >= p.y && wy <= p.y + this._cardH) return i;
    }
    return -1;
  }

  // =========================================================================
  // WHEEL
  // =========================================================================

  _onWheel = (e) => {
    if (this._isModalOpen) return;
    if (this._isOverlayTarget(e.target)) return;   // let the menu/footer scroll normally
    if (!this._isOverCanvas(e.clientX, e.clientY)) return;

    e.preventDefault();

    const rect     = this._canvas.getBoundingClientRect();
    const delta    = e.deltaY * -0.002;
    const oldScale = this._scale;
    this._scale    = Math.min(Math.max(this.SCALE_MIN, this._scale + delta), this.SCALE_MAX);

    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const cx = (this._canvas.width  / this._DPR) / 2;
    const cy = (this._canvas.height / this._DPR) / 2;

    const worldX = (mx - cx - this._offsetX) / oldScale;
    const worldY = (my - cy - this._offsetY) / oldScale;
    this._offsetX = mx - cx - worldX * this._scale;
    this._offsetY = my - cy - worldY * this._scale;
  };

  // =========================================================================
  // POINTER EVENTS
  // =========================================================================

  _onPointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (this._modalEl && this._modalEl.contains(e.target)) return;
    // Ignore clicks that land on an overlay (header menu, footer sheet, reveal
    // handle) so the products behind them aren't selected.
    if (this._isOverlayTarget(e.target)) return;
    if (!this._isOverCanvas(e.clientX, e.clientY)) return;

    this._pointerOnCanvas = true;
    this._dragDistance    = 0;
    this._pointerStart    = { x: e.clientX, y: e.clientY };

    if (this._isModalOpen) return;

    this._isDragging  = true;
    this._hoverIndex  = -1;   // hide caption while dragging; it re-appears on hover
    this._dragStartX  = e.clientX - this._offsetX;
    this._dragStartY  = e.clientY - this._offsetY;
    this._velocity    = { x: 0, y: 0 };
    document.body.style.userSelect = 'none';
    this._lastDragPos = [{ x: e.clientX, y: e.clientY, t: Date.now() }];
    this._setCursor('grabbing');
  };

  _onPointerMove = (e) => {
    if (this._isModalOpen) return;

    if (this._isDragging && this._pointerOnCanvas) {
      e.preventDefault();
      this._dragDistance = Math.hypot(
        e.clientX - this._pointerStart.x,
        e.clientY - this._pointerStart.y
      );
      this._offsetX = e.clientX - this._dragStartX;
      this._offsetY = e.clientY - this._dragStartY;
      this._lastDragPos.push({ x: e.clientX, y: e.clientY, t: Date.now() });
      if (this._lastDragPos.length > 6) this._lastDragPos.shift();
    } else if (!this._isDragging && this._isOverCanvas(e.clientX, e.clientY) && !this._isOverlayTarget(e.target)) {
      this._surfaceEl = e.target;   // the real element on top of our canvas
      const w = this._toWorld(e.clientX, e.clientY);
      this._mouseWorld = w;
      this._hoverIndex = this._cardAt(w.x, w.y);
      const hc = this._hoverIndex !== -1 ? this.refs['cards'][this._hoverIndex] : null;
      const clickable = hc && (hc.dataset.concept !== 'true' || hc.dataset.link);
      this._setCursor(clickable ? 'pointer' : 'grab');
    } else if (!this._isOverCanvas(e.clientX, e.clientY) || this._isOverlayTarget(e.target)) {
      this._mouseWorld = { x: -99999, y: -99999 };
      this._hoverIndex = -1;
    }
  };

  _onPointerUp = (e) => {
    if (!this._pointerOnCanvas) return;

    const wasDrag = this._dragDistance > this.DRAG_THRESH;
    this._isDragging      = false;
    this._pointerOnCanvas = false;
    this._setCursor('grab');
    document.body.style.userSelect = '';
    this._startMomentum();

    if (!wasDrag) {
      this._handleCanvasClick(e.clientX, e.clientY, e.pointerType);
    }
  };

  _handleCanvasClick(screenX, screenY, pointerType = 'mouse') {
    if (this._isModalOpen) {
      this._closeModal();
      return;
    }
    const w     = this._toWorld(screenX, screenY);
    const index = this._cardAt(w.x, w.y);
    if (index === -1) {
      // Empty space: on touch/pen, a quick second tap near the same spot zooms
      // toward it (a card tap opens the modal, so there's no conflict there).
      if (pointerType !== 'mouse') this._handleDoubleTap(screenX, screenY);
      return;
    }
    const card = this.refs['cards'][index];
    // Concept images aren't products: open their link if set, otherwise ignore the click.
    if (card.dataset.concept === 'true') {
      if (card.dataset.link) window.location.href = card.dataset.link;
      return;
    }
    this._isModalOpen          = true;
    this._selectedProductIndex = index;
    this._setCursor('default');
    this._showHtmlModal(card);
    if (window.innerWidth >= 750) {
      this._launchFlyingImage(card, index);
    }
  }

  _launchFlyingImage(card, index) {
    const img = card.querySelector('img');
    if (!img || !img.complete || !img.naturalWidth) return;

    const p    = this._cardPositions[index];
    const rect = this._canvas.getBoundingClientRect();
    const cW   = this._canvas.width  / this._DPR;
    const cH   = this._canvas.height / this._DPR;

    // Replicate _drawCard image position calculation
    const pad   = 20;
    const areaW = this._cardW - pad * 2;
    const areaH = this._cardH - pad * 2;
    const cardScaleRatio = parseFloat(card.dataset.cardScale || '100') / 100;
    const imgScale = cardScaleRatio ?? 0.92;
    const maxW  = areaW * imgScale;
    const maxH  = areaH * imgScale;
    const sc    = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
    const fw    = img.naturalWidth  * sc;
    const fh    = img.naturalHeight * sc;

    // World position of rendered image top-left
    const imgWX = p.x + (this._cardW - fw) / 2;
    const imgWY = p.y + pad + (areaH - fh) / 2;

    // Convert world → screen
    const toSX = wx => rect.left + cW / 2 + this._offsetX + wx * this._scale;
    const toSY = wy => rect.top  + cH / 2 + this._offsetY + wy * this._scale;

    const startX = toSX(imgWX);
    const startY = toSY(imgWY);
    const startW = fw * this._scale;
    const startH = fh * this._scale;

    // Target: the actual .ig-modal-images position in the (final) drawer.
    // offsetLeft/Top are layout-based, so they're correct even though the modal
    // is still sliding in (translateX) — its open position is left:0 / top:0.
    let targetX, targetY, targetW, targetH;
    const imagesEl = this._modalEl?.querySelector('.ig-modal-images');
    if (imagesEl) {
      targetX = imagesEl.offsetLeft;
      targetY = imagesEl.offsetTop;
      targetW = imagesEl.offsetWidth;
      targetH = imagesEl.offsetHeight;
    } else {
      const drawerW = Math.min(460, window.innerWidth * 0.9);
      targetW = drawerW;
      targetH = drawerW * 1.5;
      targetX = 0;
      targetY = Math.round((window.innerHeight - targetH) / 2);
    }

    const flying = document.createElement('div');
    flying.style.cssText = [
      'position:fixed', `z-index:10002`, 'pointer-events:none', 'overflow:hidden',
      `left:${startX}px`, `top:${startY}px`, `width:${startW}px`, `height:${startH}px`,
      'opacity:1',
      'transition:left 0.45s cubic-bezier(0.4,0,0.2,1),top 0.45s cubic-bezier(0.4,0,0.2,1),width 0.45s cubic-bezier(0.4,0,0.2,1),height 0.45s cubic-bezier(0.4,0,0.2,1),opacity 0.45s ease-in',
    ].join(';');
    flying.innerHTML = `<img src="${img.src}" style="width:100%;height:100%;object-fit:cover;display:block;">`;
    document.body.appendChild(flying);

    requestAnimationFrame(() => requestAnimationFrame(() => {
      flying.style.left    = `${targetX}px`;
      flying.style.top     = `${targetY}px`;
      flying.style.width   = `${targetW}px`;
      flying.style.height  = `${targetH}px`;
      flying.style.opacity = '0';
    }));

    // After landing: remove flying, reveal drawer image
    setTimeout(() => {
      flying.remove();
      const drawerImg = this._modalEl?.querySelector('.ig-modal-images');
      if (drawerImg) drawerImg.style.opacity = '1';
    }, 460);
  }

  _closeModal() {
    this._isModalOpen          = false;
    this._selectedProductIndex = -1;
    this._setCursor('grab');
    if (this._modalEl) {
      const modal = this._modalEl.querySelector('.ig-modal');
      const el    = this._modalEl;
      el.classList.remove('open');
      if (modal) {
        modal.classList.remove('open');
        modal.addEventListener('transitionend', () => { el.remove(); }, { once: true });
      } else {
        el.remove();
      }
      this._modalEl = null;
    }
  }

  _showHtmlModal(card) {
    const url    = card.dataset.productUrl;
    const pid    = card.dataset.productId;
    const handle = card.dataset.productHandle;
    const title  = card.querySelector('.gallery-card__title')?.textContent?.trim() || '';
    const price  = card.querySelector('.gallery-card__price')?.textContent?.trim() || '';
    const desc   = card.querySelector('.gallery-card__description')?.innerHTML?.trim() || '';
    let images   = [];
    try { images = JSON.parse(card.dataset.images || '[]'); } catch(e) {}

    if (this._modalEl) this._modalEl.remove();

    const el = document.createElement('div');
    el.className = 'ig-modal-backdrop';

    const slidesHtml = images.length
      ? images.map(src => `<div class="ig-modal-slide"><img src="${src}" alt="${title}" loading="lazy"></div>`).join('')
      : `<div class="ig-modal-slide" style="background:#f4f4f4;"></div>`;

    const arrowsHtml = images.length > 1 ? `
      <button class="ig-modal-arrow prev" aria-label="Previous">
        <svg width="10" height="18" viewBox="0 0 10 18" fill="none"><path d="M9 1L1 9L9 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button class="ig-modal-arrow next" aria-label="Next">
        <svg width="10" height="18" viewBox="0 0 10 18" fill="none"><path d="M1 1L9 9L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>` : '';

    const dotsHtml = images.length > 1
      ? `<div class="ig-modal-dots">${images.map((_, i) => `<button class="ig-modal-dot${i === 0 ? ' active' : ''}" data-index="${i}"></button>`).join('')}</div>`
      : '';

    el.innerHTML = `
      <div class="ig-modal">
        <button class="ig-modal-close" aria-label="Close">×</button>
        <div class="ig-modal-images">
          <div class="ig-modal-track">${slidesHtml}</div>
          ${arrowsHtml}
          ${dotsHtml}
        </div>
        <h2 class="ig-modal-title">${title}</h2>
        <div class="ig-modal-info">
          ${desc ? `<div class="ig-modal-desc">${desc}</div>` : ''}
          ${url ? `<a href="${url}" class="ig-modal-btn button">View product</a>` : ''}
          <button class="ig-modal-btn ig-modal-wishlist-btn button" type="button" data-product-id="${pid}" data-product-handle="${handle}" aria-label="Spara produkt">
            <span>Wishlist</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(el);
    this._modalEl = el;

    // Wishlist toggle — mirrors the shared jd_wishlist localStorage format used by
    // product-card wishlist buttons elsewhere, so state stays in sync across the page.
    const wishBtn = el.querySelector('.ig-modal-wishlist-btn');
    if (wishBtn) {
      const getWishlist  = () => { try { return JSON.parse(localStorage.getItem('jd_wishlist') || '[]'); } catch { return []; } };
      const entryMatches = (entry, id) => (typeof entry === 'string' ? entry === id : entry && entry.id === id);

      if (getWishlist().some(e => entryMatches(e, pid))) wishBtn.classList.add('active');

      wishBtn.addEventListener('click', () => {
        const list = getWishlist();
        const idx  = list.findIndex(e => entryMatches(e, pid));
        let added;
        if (idx === -1) { list.push({ id: pid, handle }); added = true; }
        else { list.splice(idx, 1); added = false; }
        localStorage.setItem('jd_wishlist', JSON.stringify(list));
        wishBtn.classList.toggle('active', added);
        document.querySelectorAll(`[data-product-id="${pid}"][class*="wishlist-btn"]`).forEach(b => b.classList.toggle('active', added));
      });
    }

    requestAnimationFrame(() => requestAnimationFrame(() => {
      el.classList.add('open');
      el.querySelector('.ig-modal')?.classList.add('open');
      if (window.innerWidth < 750) {
        const imgs = el.querySelector('.ig-modal-images');
        if (imgs) imgs.style.opacity = '1';
      }
    }));

    if (images.length > 1) {
      let current = 0;
      const track = el.querySelector('.ig-modal-track');
      const dots  = Array.from(el.querySelectorAll('.ig-modal-dot'));
      const goTo  = (i) => {
        current = (i + images.length) % images.length;
        track.style.transform = `translateX(-${current * 100}%)`;
        dots.forEach((d, idx) => d.classList.toggle('active', idx === current));
      };
      el.querySelector('.ig-modal-arrow.prev').addEventListener('click', () => goTo(current - 1));
      el.querySelector('.ig-modal-arrow.next').addEventListener('click', () => goTo(current + 1));
      dots.forEach(dot => dot.addEventListener('click', () => goTo(parseInt(dot.dataset.index))));
    }

    el.querySelector('.ig-modal-close').addEventListener('click', () => this._closeModal());
    el.addEventListener('click', (e) => { if (e.target === el) this._closeModal(); });

    // Drag-to-scroll on info panel
    const infoEl = el.querySelector('.ig-modal-info');
    if (infoEl) {
      let isDragging = false, startY = 0, startScroll = 0;
      infoEl.addEventListener('mousedown', (e) => {
        isDragging  = true;
        startY      = e.clientY;
        startScroll = infoEl.scrollTop;
        infoEl.style.cursor = 'grabbing';
        infoEl.style.userSelect = 'none';
      });
      window.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        infoEl.scrollTop = startScroll - (e.clientY - startY);
      });
      window.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        infoEl.style.cursor = '';
        infoEl.style.userSelect = '';
      });
    }
  }


  // =========================================================================
  // DOUBLE-TAP ZOOM (touch)
  // =========================================================================

  // Registers taps on empty space; a second one within 300ms and 40px toggles
  // an animated zoom toward that point (in if zoomed out, back out if already in).
  _handleDoubleTap(screenX, screenY) {
    const now  = Date.now();
    const last = this._lastTap;
    if (last && now - last.t < 300 &&
        Math.hypot(screenX - last.x, screenY - last.y) < 40) {
      const zoomedIn    = this._scale > (this.SCALE_MIN + this.SCALE_MAX) / 2;
      const targetScale = zoomedIn
        ? this.SCALE_MIN
        : Math.min(this.SCALE_MAX, this.SCALE_MIN * 2);
      this._animateZoomTo(targetScale, screenX, screenY);
      this._lastTap = null;
    } else {
      this._lastTap = { x: screenX, y: screenY, t: now };
    }
  }

  // Eases _scale toward a target while keeping the world point under (screenX,
  // screenY) pinned, so the tapped spot stays put as the view zooms.
  _animateZoomTo(targetScale, screenX, screenY) {
    const rect = this._canvas.getBoundingClientRect();
    const mx   = screenX - rect.left;
    const my   = screenY - rect.top;
    const cx   = (this._canvas.width  / this._DPR) / 2;
    const cy   = (this._canvas.height / this._DPR) / 2;
    const worldX = (mx - cx - this._offsetX) / this._scale;
    const worldY = (my - cy - this._offsetY) / this._scale;

    const startScale = this._scale;
    const startT     = performance.now();
    const dur        = 260;
    const ease       = (t) => 1 - Math.pow(1 - t, 3);

    const step = () => {
      if (this._isDragging || this._pinchActive) return;   // let a new gesture take over
      const t = Math.min(1, (performance.now() - startT) / dur);
      this._scale   = startScale + (targetScale - startScale) * ease(t);
      this._offsetX = mx - cx - worldX * this._scale;
      this._offsetY = my - cy - worldY * this._scale;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // =========================================================================
  // TOUCH (pinch to zoom)
  // =========================================================================

  _onTouchStart = (e) => {
    if (e.touches.length !== 2) return;
    e.preventDefault();
    this._pinchActive     = true;
    this._isDragging      = false;
    this._pinchStartDist  = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY
    );
    this._pinchStartScale = this._scale;
  };

  _onTouchMove = (e) => {
    if (!this._pinchActive || e.touches.length !== 2) return;
    e.preventDefault();

    const rect = this._canvas.getBoundingClientRect();
    const dist = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY
    );
    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
    const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
    const cx   = (this._canvas.width  / this._DPR) / 2;
    const cy   = (this._canvas.height / this._DPR) / 2;

    const oldScale = this._scale;
    const worldX   = (midX - cx - this._offsetX) / oldScale;
    const worldY   = (midY - cy - this._offsetY) / oldScale;

    this._scale   = Math.min(Math.max(this.SCALE_MIN, this._pinchStartScale * (dist / this._pinchStartDist)), this.SCALE_MAX);
    this._offsetX = midX - cx - worldX * this._scale;
    this._offsetY = midY - cy - worldY * this._scale;
  };

  _onTouchEnd = (e) => {
    if (e.touches.length < 2) {
      this._pinchActive     = false;
      this._pinchStartScale = this._scale;
    }
  };

  // =========================================================================
  // MOMENTUM
  // =========================================================================

  _startMomentum() {
    const FRICTION = 0.91;
    const MIN_V    = 0.4;

    const recent = this._lastDragPos.filter(p => Date.now() - p.t < 100);
    if (recent.length >= 2) {
      const a = recent[0], b = recent[recent.length - 1];
      const dt = Math.max(b.t - a.t, 1);
      this._velocity.x = ((b.x - a.x) / dt) * 16 * 0.65;
      this._velocity.y = ((b.y - a.y) / dt) * 16 * 0.65;
    }

    if (Math.abs(this._velocity.x) < MIN_V && Math.abs(this._velocity.y) < MIN_V) return;

    const tick = () => {
      if (this._isDragging) return;
      this._velocity.x *= FRICTION;
      this._velocity.y *= FRICTION;
      if (Math.abs(this._velocity.x) < MIN_V && Math.abs(this._velocity.y) < MIN_V) {
        this._velocity = { x: 0, y: 0 };
        return;
      }
      this._offsetX += this._velocity.x;
      this._offsetY += this._velocity.y;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  // =========================================================================
  // MOBILE LOOKBOOK (vertical snap-scroll + parallax)
  // =========================================================================

  _initMobileFeed() {
    // Wishlist page reuses this feed, so filter to hearted products first.
    if (this._isWishlistOnly) this._filterToWishlist();
    this._shuffleInPlace(this.refs['cards']);
    this._buildMobileFeed();
    window.addEventListener('resize', this._onMobileResize);
  }

  _mobileHeaderH() {
    const header = document.querySelector('.shopify-section-group-header-group');
    return header ? header.offsetHeight : 0;
  }

  // "16,000.00 kr" → "SEK 16 000": drop the öre, swap the kr suffix for a SEK prefix,
  // regroup thousands with a NO-BREAK space (U+00A0). A plain space would let the
  // number break across two lines at the end of the caption row; U+00A0 over the
  // narrower U+202F because it exists in every font the slide can fall back to.
  _formatMobilePrice(raw) {
    if (!raw) return '';
    let n = raw.replace(/[^\d.,\s]/g, '').trim();   // drop currency letters/symbols
    n = n.replace(/[.,]\d{2}\s*$/, '');             // drop trailing .00 / ,00
    n = n.replace(/[^\d]/g, '');                    // digits only
    if (!n) return raw;
    n = n.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `SEK ${n}`;
  }

  /* — Publish the photo's painted rect so the large title, caption and progress bar
       can hug it. The photo is aspect-locked (2:3) and width-capped, so its <img>
       BOX is not the picture: object-fit:contain letterboxes the pixels inside it,
       and offsetHeight sees only the box. Replay contain's maths to get the real
       rect. offsetTop/offsetHeight (not getBoundingClientRect) so a measure taken
       mid-scroll isn't polluted by the parallax transform on _media. — */
  _measureMobilePhoto(slide) {
    const im = slide._img, md = slide._media;
    if (!im || !md) return;
    const bw = im.offsetWidth, bh = im.offsetHeight;
    if (!bh) return;                     // not laid out yet — a later pass catches it
    let top = md.offsetTop + im.offsetTop, h = bh, w = bw;
    const nw = im.naturalWidth, nh = im.naturalHeight;
    if (nw && nh) {
      const scale = Math.min(bw / nw, bh / nh);
      h = scale * nh;
      w = scale * nw;
      top += (bh - h) / 2;
    }
    slide.style.setProperty('--ig-ph-top', Math.round(top) + 'px');
    slide.style.setProperty('--ig-ph-bot', Math.round(top + h) + 'px');
    slide.style.setProperty('--ig-ph-w', Math.round(w) + 'px');
  }
  // Where the caption ends — the price hangs off this, so a two-line title can't run
  // through it. Must be re-run whenever the photo vars move: the caption's own top is
  // derived from --ig-ph-bot, so a stale --ig-ph-cap strands the price.
  _measureMobileCaption(slide) {
    const inf = slide._info;
    if (!inf || !inf.offsetHeight) return;
    slide.style.setProperty('--ig-ph-cap', (inf.offsetTop + inf.offsetHeight) + 'px');
  }
  _measureMobileSlide(slide) {
    this._measureMobilePhoto(slide);
    this._measureMobileCaption(slide);
  }
  // Two passes: pass 1 writes every photo var, pass 2 reads the caption boxes those
  // vars just moved. Batched this way the browser does two layout flushes in total
  // rather than one per slide.
  _measureAllMobile() {
    if (!this._mobileSlides) return;
    this._mobileSlides.forEach(s => this._measureMobilePhoto(s));
    this._mobileSlides.forEach(s => this._measureMobileCaption(s));
  }

  // Style the lookbook panel as a `position: fixed` scroll box pinned under the
  // header. It lives on document.body (NOT inside <interactive-gallery>) for two
  // reasons: (1) body has no transformed ancestor, so `fixed` resolves against the
  // viewport — the theme's transform-trap can't reparent it; (2) it dodges the
  // locked body-grid that clips <main> to zero height. All inline, because the JS
  // asset is cache-busted (?v=) while the {% stylesheet %} bundle is not.
  _stylePanel(panel) {
    const hpx = this._mobileHeaderH();
    Object.assign(panel.style, {
      position: 'fixed',
      top: hpx + 'px',
      left: '0',
      right: '0',
      bottom: '0',
      overflowY: 'auto',
      overflowX: 'hidden',
      // 'manipulation', NOT 'pan-y': touch-action INTERSECTS down the tree, so a
      // pan-y panel forbids horizontal panning in every descendant regardless of what
      // they declare — the image strip inside each slide could never be swiped.
      // manipulation permits both axes and still drops the double-tap zoom delay,
      // which is what pan-y was really buying here.
      touchAction: 'manipulation',
      scrollSnapType: 'y mandatory',   // always land on a product (footer is a full-height snap target)
      webkitOverflowScrolling: 'touch',
      // Header colour without the header's blur: behind this panel is a STATIC body
      // backdrop, so blur() would do nothing visible but wreck scroll perf (blurring
      // a scrolling container repaints every frame). 50% white over the same body-bg
      // still matches the header's tone.
      backgroundColor: 'rgba(255,255,255,0.5)',
      zIndex: '1',
    });
    // Read back the resolved height (forces layout) so slides match it exactly.
    this._mobileSlideHpx = panel.clientHeight || Math.max(320, window.innerHeight - hpx);
  }

  _onMobileResize = () => {
    if (!this._mobilePanel) return;
    this._mobilePanel.style.top = this._mobileHeaderH() + 'px';
    this._mobileSlideHpx = this._mobilePanel.clientHeight || this._mobileSlideHpx;
    if (this._mobileSlides) this._mobileSlides.forEach(s => { s.style.height = this._mobileSlideHpx + 'px'; });
    this._measureAllMobile();   // slide height changed → so did the photo box
    this._updateMobileParallax();
  };

  _buildMobileFeed() {
    const cards = this.refs['cards'];

    // The visible lookbook is a body-level fixed panel; the custom element itself is
    // hidden and only serves as the data source (its card data is still readable).
    this.style.display = 'none';

    const panel = document.createElement('div');
    panel.className = 'ig-mobile-panel';
    this._mobilePanel = panel;
    this._stylePanel(panel);   // sets this._mobileSlideHpx from the panel's box

    this._mobileSlides = [];

    cards.forEach((card, i) => {
      if (card.dataset.concept === 'true') return;   // concept images aren't products

      let imgs = [];
      try { imgs = JSON.parse(card.dataset.images || '[]'); } catch {}
      const hero  = imgs[0] || card.querySelector('img')?.dataset.src || '';
      const title = card.querySelector('.gallery-card__title')?.textContent?.trim() || '';
      const price = card.querySelector('.gallery-card__price')?.textContent?.trim() || '';
      const largeTxt = (card.dataset.largeTitle || '').trim();   // custom.large_title metafield

      const material = (card.dataset.material || '').trim();

      const slide = document.createElement('article');
      slide.className = 'ig-mslide';
      slide.style.cssText =
        `position:relative;width:100%;height:${this._mobileSlideHpx}px;` +
        `scroll-snap-align:start;overflow:hidden;background:transparent;cursor:pointer;`;

      // ── PHOTO BAND ─ a horizontal snap strip, one full-width frame per image.
      // Single-image products get a plain flex box, so nothing about them changes.
      // No touch-action is set anywhere in here: touch-action intersects down the
      // tree, so pinning an axis would forbid the other one for the whole slide.
      const media = document.createElement('div');
      media.className = 'ig-mslide__media';
      const swipeable = imgs.length > 1;
      const band =
        'position:absolute;left:0;right:0;' +
        'top:var(--ig-m-photo-top,8%);height:var(--ig-m-photo-band,80%);';
      media.style.cssText = swipeable
        ? band + 'display:block;white-space:nowrap;overflow-x:auto;overflow-y:hidden;' +
                 'scroll-snap-type:x mandatory;overscroll-behavior-x:contain;' +
                 '-webkit-overflow-scrolling:touch;scrollbar-width:none;'
        : band + 'display:flex;align-items:center;justify-content:center;';

      const srcs = imgs.length ? imgs : (hero ? [hero] : []);
      let img = null;
      srcs.forEach((src, fi) => {
        const frame = document.createElement('div');
        frame.className = 'ig-mframe';
        frame.style.cssText = swipeable
          ? 'display:inline-flex;vertical-align:top;align-items:center;justify-content:center;' +
            'width:100%;height:100%;scroll-snap-align:center;scroll-snap-stop:always;'
          : 'display:flex;align-items:center;justify-content:center;width:100%;height:100%;';
        const im = document.createElement('img');
        im.className = 'ig-mphoto';
        im.src = src;
        im.alt = title;
        im.decoding = 'async';
        // Frame 1 keeps the old rule (first two slides eager, no top flash); the rest
        // only matter once the strip is swiped.
        if (fi > 0 || i >= 2) im.loading = 'lazy';
        // Whole image in a 2:3 frame (contain) — uncropped, with air around it.
        // transform:none is explicit: the {% stylesheet %} bundle is NOT cache-busted,
        // so a stale cached copy can still carry the old `transform:scale(0.72)` here;
        // clearing it inline (the JS asset IS cache-busted) guarantees no shrinking.
        im.style.cssText =
          'height:100%;max-width:94%;width:auto;aspect-ratio:2/3;object-fit:contain;display:block;transform:none;';
        if (fi === 0) {
          img = im;
          // No naturalWidth until it decodes, so the build-time measure falls back to
          // the box and the caption sits at the band's bottom. Re-measure BOTH the
          // photo and the caption on arrival — photo-only would strand the price.
          im.addEventListener('load', () => this._measureMobileSlide(slide), { once: true });
        }
        frame.appendChild(im);
        media.appendChild(frame);
      });

      // ── CAPTION ─ product title + material, hugging the photo's real bottom edge.
      const info = document.createElement('div');
      info.className = 'ig-mslide__info';
      info.style.cssText =
        'position:absolute;left:0;right:0;' +
        'top:calc(var(--ig-ph-bot,88%) + var(--ig-m-cap-gap,12px));' +
        'padding:0 20px;text-align:center;pointer-events:none;';
      const h = document.createElement('h3');
      h.className = 'ig-mslide__title';
      h.textContent = title;
      // Clamped to 2 lines. At 23px a 62-character product title (the longest on the
      // page) runs to three lines, and the caption block then pushed the price past
      // the slide's bottom edge — which is overflow:hidden — on every device tested.
      // The clamp makes the caption's height bounded instead of title-dependent.
      h.style.cssText =
        "margin:0;line-height:1.05;" +
        "display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;" +
        "font-family:var(--ig-m-title-font,'GalleryTitle','Dymo',cursive);" +
        "font-size:var(--ig-m-title-size,23px);" +
        "color:var(--ig-m-title-color,rgb(51,56,60));";
      info.append(h);

      // Material (custom.title2) — inside the caption block, so the measured caption
      // height covers it and the price drops below it on its own.
      if (material) {
        const mt = document.createElement('p');
        mt.className = 'ig-mslide__material';
        mt.textContent = material;
        mt.style.cssText =
          'margin:10px 0 0;text-align:right;line-height:1.1;' +
          "font-family:var(--ig-m-material-font,'GalleryTitle','Dymo',cursive);" +
          'font-size:var(--ig-m-material-size,11px);' +
          'color:var(--ig-m-material-color,rgba(17,17,17,0.6));';
        info.append(mt);
      }

      // Price — under the caption's real bottom edge, right-aligned. Anchored to the
      // caption (not the slide bottom) so a two-line title can never run through it.
      const pr = document.createElement('p');
      pr.className = 'ig-mslide__price';
      pr.textContent = this._formatMobilePrice(price);
      pr.style.cssText =
        'position:absolute;right:20px;margin:0;pointer-events:none;' +
        'top:calc(var(--ig-ph-cap,calc(var(--ig-ph-bot,88%) + 28px)) + var(--ig-m-price-gap,14px));' +
        "font-family:var(--ig-m-price-font,'GalleryTitle','Dymo',cursive);" +
        "font-size:var(--ig-m-price-size,15px);" +
        "color:var(--ig-m-price-color,rgb(51,56,60));";

      slide.append(media, info, pr);

      // ── IMAGE PROGRESS ─ a hairline along the picture's bottom edge, one segment
      // per photo. Width is the PAINTED width (--ig-ph-w), not the letterboxed box.
      let bar = null;
      if (swipeable) {
        bar = document.createElement('div');
        bar.className = 'ig-mbar';
        bar.style.cssText =
          'position:absolute;left:50%;transform:translateX(-50%);' +
          'width:var(--ig-ph-w,94%);top:calc(var(--ig-ph-bot,88%) - 2px);' +
          'display:flex;gap:3px;height:2px;pointer-events:none;';
        for (let s = 0; s < srcs.length; s++) {
          const seg = document.createElement('span');
          seg.className = 'ig-mseg';
          seg.style.cssText =
            'flex:1;height:100%;transition:background 0.15s ease;background:' +
            (s === 0 ? 'var(--ig-m-bar-color-on,rgba(17,17,17,0.75))'
                     : 'var(--ig-m-bar-color,rgba(17,17,17,0.18))');
          bar.appendChild(seg);
        }
        slide.appendChild(bar);
        let bRaf = null;
        media.addEventListener('scroll', () => {
          if (bRaf) return;
          bRaf = requestAnimationFrame(() => {
            bRaf = null;
            const at = Math.round(media.scrollLeft / (media.clientWidth || 1));
            Array.from(bar.children).forEach((seg, si) => {
              seg.style.background = si === at
                ? 'var(--ig-m-bar-color-on,rgba(17,17,17,0.75))'
                : 'var(--ig-m-bar-color,rgba(17,17,17,0.18))';
            });
          });
        }, { passive: true });
      }

      // Large title (custom.large_title) — decorative, straddling the photo's top
      // edge: half above it, half over the image. Appended LAST so it paints on top
      // of the photo, and kept OUT of _info so the parallax never fades it.
      let largeWrap = null;
      if (largeTxt && largeTxt !== title) {
        largeWrap = document.createElement('div');
        largeWrap.className = 'ig-mslide__large-wrap';
        largeWrap.style.cssText =
          'position:absolute;left:0;right:0;top:var(--ig-ph-top,8%);' +
          'transform:translateY(-50%);padding:0 20px;text-align:center;pointer-events:none;';
        const lh = document.createElement('h2');
        lh.className = 'ig-mslide__large-title';
        lh.textContent = largeTxt;
        lh.style.cssText =
          "margin:0;line-height:1.05;" +
          "font-family:var(--ig-m-large-title-font,'BenguiatProITC-Book',serif);" +
          "font-size:var(--ig-m-large-title-size,32px);" +
          "color:var(--ig-m-large-title-color,rgb(51,56,60));";
        largeWrap.appendChild(lh);
        slide.appendChild(largeWrap);
      }

      slide._media = media;
      slide._info  = info;
      slide._img   = img;
      // Mobile: tapping a product goes straight to its product page (no modal). A
      // swipe across the image strip must not count as a tap — a scroll gesture does
      // not reliably suppress the click, and opening a product by accident while
      // browsing its photos is the worst outcome here.
      let downX = 0, downY = 0;
      slide.addEventListener('pointerdown', (e) => { downX = e.clientX; downY = e.clientY; }, { passive: true });
      slide.addEventListener('click', (e) => {
        if (Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY) > 10) return;
        const dest = card.dataset.productUrl || card.dataset.link;
        if (dest) window.location.href = dest;
      });

      panel.appendChild(slide);
      this._mobileSlides.push(slide);
    });

    document.body.appendChild(panel);
    this._mobileScrollEl = panel;
    panel.addEventListener('scroll', this._onMobileScroll, { passive: true });
    requestAnimationFrame(() => {
      // Panel had no laid-out height until it was in the DOM; lock slide heights now.
      this._mobileSlideHpx = panel.clientHeight || this._mobileSlideHpx;
      this._mobileSlides.forEach(s => { s.style.height = this._mobileSlideHpx + 'px'; });
      this._measureAllMobile();   // heights are final → the photo rects are final
      this._updateMobileParallax();
    });

    // A webfont swapping in re-wraps the title, which changes the caption height the
    // price hangs off. Measure again once the fonts have settled.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._measureAllMobile());
    }
  }

  _onMobileScroll = () => {
    if (this._mobileRaf) return;
    this._mobileRaf = requestAnimationFrame(() => {
      this._mobileRaf = null;
      this._updateMobileParallax();
    });
  };

  // Every slide is exactly the scroll container's height, so scrollTop / H is the
  // fractional slide index — no getBoundingClientRect per frame. Only the slide at
  // centre and its two neighbours need updating.
  _updateMobileParallax() {
    const el = this._mobileScrollEl;
    if (!el || !this._mobileSlides || !this._mobileSlides.length) return;
    const H = this._mobileSlides[0].offsetHeight || el.clientHeight || 1;
    const s = el.scrollTop / H;
    const base = Math.floor(s);
    const PARALLAX = 42;   // px the photo drifts within its taller frame
    for (let i = base - 1; i <= base + 1; i++) {
      const slide = this._mobileSlides[i];
      if (!slide) continue;
      const p = i - s;   // +below … 0 centred … −above
      slide._media.style.transform = `translate3d(0, ${(-p * PARALLAX).toFixed(1)}px, 0)`;
      slide._info.style.opacity    = Math.max(0, 1 - Math.abs(p) * 1.5).toFixed(3);
      slide._info.style.transform  = `translateY(${(p * 18).toFixed(1)}px)`;
    }
  }

  _openMobileModal(card) {
    if (this._isModalOpen) return;
    this._isModalOpen = true;
    this._showHtmlModal(card);   // appends the bottom-sheet to document.body
  }
}

if (!customElements.get('interactive-gallery')) {
  customElements.define('interactive-gallery', InteractiveGallery);
}
